import { BaseElementWithoutShadowDOM } from '@theme/base-element'
import '@theme/ui-product-form'
import '@theme/ui-quantity-input'
import '@theme/ui-variant-picker'
import { VARIANT_PICKER_CHANGE_EVENT } from '@theme/ui-variant-picker'
import HTMLUpdateUtility from '@theme/mixin-html-update-utility'

export class MainProduct extends BaseElementWithoutShadowDOM {
  constructor() {
    super(...arguments)

    this.$htmlUpdateUtility = new HTMLUpdateUtility(this)
    this.placement = 'product-card'

    /**
     * Handles the click event on the product component and sends analytics data to Shopify.
     *
     * This function retrieves product analytics data from a `<script>` tag with `data-analytics`,
     * parses the JSON content, and publishes an `analytics:product_card_click` event using Shopify's
     * analytics API. This allows Shopify's analytics system to track product clicks for reporting
     * and insights.
     */
    this.trackProductClickAnalytics = async (event) => {
      const target = event.target
      const isLinkClick = target.closest('a')

      if (!isLinkClick) {
        return
      }

      const analyticsScript = this.querySelector('script[data-analytics]')

      if (!analyticsScript) {
        console.warn('Analytics script not found')
        return
      }

      const analyticsDataText = analyticsScript.textContent?.trim()

      if (!analyticsDataText) {
        console.warn('Analytics script is empty')
        return
      }

      event.preventDefault()

      const analyticsDataJson = JSON.parse(analyticsDataText)
      await this.sendAnalyticsData(analyticsDataJson)

      window.location.href = isLinkClick.getAttribute('href')
    }
  }
  static get properties() {
    return {
      sectionId: { type: String, attribute: 'section-id' },
      productUrl: { type: String, attribute: 'product-url' },
      variantId: { type: String, attribute: 'variant-id' },
      view: { type: String, attribute: 'view' },
      analytics: { type: Boolean, attribute: 'analytics' },
      placement: { type: String, attribute: 'placement' },
      shouldUpdateUrl: {
        type: String,
        attribute: 'update-url',
        converter: (value) => value === 'true',
      },
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.handleVariantPickerChange = this.handleVariantPickerChange.bind(this)
    this.handleStickyVariantChange = this.handleStickyVariantChange.bind(this)
    this.handleDesktopScroll()
    this.addEventListener(
      VARIANT_PICKER_CHANGE_EVENT,
      this.handleVariantPickerChange
    )
    this.addEventListener('change', this.handleStickyVariantChange)

    if (this.placement === 'pdp') {
      document.addEventListener('cart:updated', () => this.handleCartUpate())
    }

    this.$htmlUpdateUtility.addPostProcessCallback(() => {
      window?.Shopify?.PaymentButton?.init()
    })

    this.$htmlUpdateUtility.addPostProcessCallback((html) =>
      this.refreshTolstoyWidgets(html)
    )

    if (this.analytics) {
      this.addEventListener('click', this.trackProductClickAnalytics)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener(
      VARIANT_PICKER_CHANGE_EVENT,
      this.handleVariantPickerChange
    )
    this.removeEventListener('change', this.handleStickyVariantChange)

    this.removeEventListener('click', this.trackProductClickAnalytics)
  }

  // Re-initialise Tolstoy video widgets inside HTML swapped in after a variant change
  refreshTolstoyWidgets(html) {
    html
      .querySelectorAll('tolstoy-stories[data-publish-id]')
      .forEach((element) => {
        const publishId = element.dataset.publishId
        const widget = publishId ? window.tolstoyWidget?.[publishId] : undefined
        if (!widget?.init) return
        try {
          widget.init({
            productId: element.dataset.productId,
            tags: element.dataset.tags,
          })
        } catch (error) {
          console.warn('Tolstoy widget refresh failed', error)
        }
      })
  }

  updateURL(url, variantId) {
    if (!this.shouldUpdateUrl) return
    const params = new URLSearchParams()
    if (variantId) params.set('variant', variantId)
    // Keep ?view= (quick-shop and other alternate templates) when the shade changes
    if (this.view) params.set('view', this.view)
    const query = params.toString()
    window.history.replaceState({}, '', `${url}${query ? `?${query}` : ''}`)
  }

  handleDesktopScroll() {
    let buyButtons = this.querySelector('#buy-buttons')
    const footer = document.querySelector('#main-footer')

    if (!buyButtons || !footer) {
      return
    }

    const intersectionOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    }

    let isBuyButtonsVisible = true
    let isFooterVisible = false

    const updateStickyState = () => {
      if (this.view === 'quick-shop' || !buyButtons) {
        return
      }

      const isStickyActive = !isBuyButtonsVisible && !isFooterVisible
      const ccRevokeBtn = document.querySelector('.cc-revoke.cc-bottom.cc-left')

      if (isStickyActive) {
        buyButtons.classList.add('sticky-atc')
        if (ccRevokeBtn) {
          ccRevokeBtn.style.bottom = '100px'
        }
      } else {
        buyButtons.classList.remove('sticky-atc')
        if (ccRevokeBtn) {
          ccRevokeBtn.style.bottom = ''
        }
      }
    }

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.target === buyButtons) {
          isBuyButtonsVisible = entry.isIntersecting
        } else if (entry.target === footer) {
          isFooterVisible = entry.isIntersecting
        }
      })
      updateStickyState()
    }

    const observer = new IntersectionObserver(
      observerCallback,
      intersectionOptions
    )

    observer.observe(buyButtons)
    observer.observe(footer)

    // Variant/sibling changes re-render the buy buttons: the element can be swapped
    // for a new one and its class attribute is reset, which drops `sticky-atc`.
    // Re-attach the observer to the current element and re-apply the sticky state.
    this.$htmlUpdateUtility.addPostProcessCallback(() => {
      const currentBuyButtons = this.querySelector('#buy-buttons')

      if (currentBuyButtons !== buyButtons) {
        if (buyButtons) observer.unobserve(buyButtons)
        buyButtons = currentBuyButtons
        if (buyButtons) observer.observe(buyButtons)
      }

      updateStickyState()
    })
  }

  // Shade/variant dropdown inside the mobile sticky add to cart
  handleStickyVariantChange(event) {
    const select = event.target

    if (
      !(select instanceof HTMLSelectElement) ||
      !select.matches('[data-sticky-variant-select]')
    ) {
      return
    }

    const option = select.selectedOptions[0]
    if (!option) return

    const { variantId, productUrl, variantAvailable, label, image } =
      option.dataset

    // Unavailable sibling: load its product page, same as the swatch picker
    if (
      variantAvailable === 'false' &&
      productUrl &&
      productUrl !== this.productUrl
    ) {
      const nextParams = new URLSearchParams()
      if (variantId) nextParams.set('variant', variantId)
      if (this.view) nextParams.set('view', this.view)
      window.location.href = `${productUrl}?${nextParams.toString()}`
      return
    }

    // Reflect the choice straight away while the section re-renders
    const pill = select.closest('.sticky-atc-variant__pill')
    const labelElement = pill?.querySelector('[data-sticky-variant-label]')
    const imageElement = pill?.querySelector('[data-sticky-variant-image]')
    if (labelElement && label) labelElement.textContent = label
    if (imageElement && image) imageElement.src = image

    this.handleVariantPickerChange({ detail: { variantId, productUrl } })
  }

  handleVariantPickerChange(event) {
    const newProductUrl = event.detail.productUrl
    const targetVariantId = event.detail.variantId

    if (!targetVariantId || targetVariantId.trim() === '') return
    this.updateURL(newProductUrl ?? '', targetVariantId)
    // Keep the current variant so cart-triggered re-renders use the new selection
    this.variantId = targetVariantId

    if (
      newProductUrl &&
      newProductUrl !== '' &&
      newProductUrl !== this.productUrl
    ) {
      this.productUrl = newProductUrl
    }

    this.updateProductDetails({
      productUrl: newProductUrl ? newProductUrl : this.productUrl,
      variantId: targetVariantId,
    })
  }

  handleCartUpate() {
    this.updateProductDetails({
      productUrl: this.productUrl,
      variantId: this.variantId,
      withCache: false,
    })
  }

  updateProductDetails({ productUrl, variantId, withCache = true }) {
    this.$htmlUpdateUtility
      .fetchAndReplaceSectionId({
        renderRoot: this,
        sectionId: this.sectionId,
        baseUrl: productUrl,
        params: {
          variant: variantId,
          ...(this.view ? { view: this.view } : {}),
        },
        withCache,
      })
      .catch((error) => {
        if (error instanceof Error) {
          throw error
        } else {
          console.warn(error)
        }
      })
  }

  sendAnalyticsData(data) {
    return new Promise((resolve) => {
      window.Shopify?.analytics?.publish('analytics:product_card_click', data)
      setTimeout(() => {
        resolve()
      }, 1000)
    })
  }
}

customElements.define('main-product', MainProduct)
